import {
  PerspectiveCamera,
  WebGLRenderer,
  Mesh,
  AmbientLight,
  ShaderMaterial,
  CubeTextureLoader,
  OrthographicCamera,
  PlaneGeometry,
  WebGLRenderTarget,
  RepeatWrapping,
  FloatType,
  Vector2,
  AdditiveBlending,
  Scene,
  NoBlending,
  SphereGeometry,
  WebGLCubeRenderTarget,
  CubeCamera,
  LinearMipmapLinearFilter,
  RGBFormat,
  Vector4,
  BackSide,
  Vector3,
  MathUtils,
  PCFSoftShadowMap,
  SpotLight,
  BoxGeometry,
  Quaternion
} from 'three';

import { updatePointLightPosition } from '../utils/lightClose.js';
import { GuiInterface } from '../gui/gui.js';
import { findClosestBody, updateCamFor, detectCollision } from '../utils/cameraUtils.js';
import { setBodyGrid, createAstroGrid } from '../utils/gridSpaceTime.js';
import { createCommonMagneticField, updateCommonMagneticField, updateMagneticFieldAnimation } from '../utils/magneticPoloidal.js';

import distortionHoleVertex from "../../../shaders/distortionHole/vertexDist.glsl";
import distortionHoleFragment from "../../../shaders/distortionHole/fragmentDist.glsl";
import fragmentCube from '../../../shaders/noisesCube/fragment.glsl';
import vertexCube from '../../../shaders/noisesCube/vertex.glsl';
import noisesVertex from '../../../shaders/noises/vertex.glsl';
import noisesFragment from '../../../shaders/noises/fragment.glsl';
import compositionVertex from '../../../shaders/composition/vertex.glsl';
import compositionFragment from '../../../shaders/composition/fragment.glsl';

import nx from '../../../assets/scenario/background/nx.png';
import ny from '../../../assets/scenario/background/ny.png';
import nz from '../../../assets/scenario/background/nz.png';
import px from '../../../assets/scenario/background/px.png';
import py from '../../../assets/scenario/background/py.png';
import pz from '../../../assets/scenario/background/pz.png';

import { FlyControls } from 'three/examples/jsm/controls/FlyControls.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

export class SceneManager {
  constructor() {
    this.canvas = document.querySelector('canvas.webgl');
    const gl2 = this.canvas.getContext('webgl2', { antialias: true });
    this.sizes = {
      width: window.innerWidth - 20,
      height: window.innerHeight - 20,
    };

    this.scene = new Scene();
    this.camera = new PerspectiveCamera(
      50,
      this.sizes.width / this.sizes.height,
      1e-5,
      2e11
    );
    this.sizeDistortion = 8.05;
    this.dimension = false;
    this.renderer = new WebGLRenderer({
      antialias: true,
      canvas: this.canvas,
      context: gl2,
      powerPreference: 'high-performance',
      logarithmicDepthBuffer: true,
      alpha: true,
    });

    if (this.renderer.getContext().getExtension('EXT_color_buffer_float')) {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = PCFSoftShadowMap;
     
    }

    this.orbitC = new OrbitControls(this.camera, this.canvas);
    this.fly = new FlyControls(this.camera, this.canvas);
    this.camera.position.set(18.81, 0, -13.39);
    this.fly.movementSpeed = 10;
    const canvas = this.renderer.domElement;
    canvas.addEventListener('mouseenter', () => {
      this.fly.enabled = true;
    });
    canvas.addEventListener('mouseleave', (e) => {
      const to = e.relatedTarget;
      if (canvas.contains(to) || to?.closest('.label')) {
        return;
      }
      this.fly.enabled = false;
    });


    this.addTexture();
    this.createHoleDistortion();
    this.init();
    
    this.spotLight = new SpotLight(0xffffff, 2.5, 2, Math.PI / 3, 0.0001, 1e-2);
    this.spotLight.castShadow = true;
    this.spotLight.shadow.mapSize.set(2048, 2048);
    this.spotLight.shadow.camera.near = 0.1;
    this.spotLight.shadow.camera.far = 2;
    this.spotLight.shadow.bias = -0.0003;
    this.spotLight.shadow.normalBias = 0;
    this.spotLight.shadow.focus = 0.0137
  
    this.scene.add(this.spotLight,  this.spotLight.target );

    this.ambientLight = new AmbientLight(0x99999999, 0.5);
    this.scene.add(this.ambientLight);
    this.createGui();

    window.addEventListener('resize', () => {
      this.sizes.width = window.innerWidth - 20;
      this.sizes.height = window.innerHeight - 20;
      this.configRenderCompo();
    });

    document.body.appendChild(this.renderer.domElement);

    this.cubeTextureLoader = new CubeTextureLoader(loading.loadingManager);
    this.scene.background = this.cubeTextureLoader.load([px, nx, py, ny, pz, nz]);


    this.createLabel();

    this.bodies = null;
    this.firstBody = null;
    this.gridSpaceTime = null;
    this.magneticField = null;
    this.lastMagnetizedObject = null;
    this.smoothDelta = 0;
    this.lastTime = null;
    this.SMOOTHING_FACTOR = 0.1;
    this.MAX_DELTA = 0.5;
    this.speed = 0;
  }

  setBodies(bodies, firstBody) {
    this.bodies = bodies;
    this.sun = this.bodies.sun;
    this.firstBody = firstBody;
    this.gridSpaceTime = null;
    this.magneticField = null;
    this.lastMagnetizedObject = null;
    this.lastObjectLight = this.bodies.earth;
    this.smoothDelta = 0;
    this.lastTime = performance.now();
  }
  
  startAnimationLoop() {
    this.renderer.setAnimationLoop(this.animate.bind(this));
  }

  animate(realTimestamp) {
    if (!this.bodies) return;
    const now = realTimestamp;
    let rawDelta = (now - this.lastTime) / 1000;
    this.lastTime = now;
    rawDelta = Math.min(rawDelta, this.MAX_DELTA);
    this.smoothDelta = MathUtils.lerp(this.smoothDelta, rawDelta, this.SMOOTHING_FACTOR);

    const controls = this.commands.controls;
    const timeDirection = this.dimension ? -1 : 1;
    const baseSpeed = this.smoothDelta * this.commands.timeUnits[controls.timeUnit];
    this.speed = baseSpeed * timeDirection;

    const selectBody = this.bodies[controls.camTarget];
    const closerObj = findClosestBody(this.camera, this.bodies);
    const bodyClose = this.bodies[closerObj.labCloser];


    if(this.commands.controls.labels){
    this.commands.setInfoContainer(selectBody.div);
    }
    


    this.minDist = closerObj.minDist

    if(Math.abs(bodyClose.distScale)<0.3)
    detectCollision(bodyClose);

    Object.keys(this.bodies).forEach(key => {
      this.bodies[key].animate(this.speed);
    });

    if (bodyClose.haveMoon) {
      this.lastObjectLight = bodyClose;
    }else if(bodyClose.isMoon){
      this.lastObjectLight = bodyClose.centerElement;
    }
    
    updatePointLightPosition(
      this.spotLight,
      this.lastObjectLight,
      this.sun,
      this.camera
    );
  
    this.sun.pointLight.visible = !(bodyClose.haveMoon || bodyClose.isMoon);
    
    this.commands.followCam(selectBody);
    updateCamFor(controls, selectBody, this.camera, closerObj.minDist);
    if (!this.gridSpaceTime) {
      this.gridSpaceTime = createAstroGrid(selectBody, this.firstBody);
    } else {
      const objSelectPos = new Vector3();
      selectBody.mesh.getWorldPosition(objSelectPos);
      const computedDistance = this.camera.position.distanceTo(objSelectPos);
      setBodyGrid(this.gridSpaceTime, selectBody, controls, computedDistance, this.firstBody);
    }
    if (!this.magneticField) {
      this.magneticField = createCommonMagneticField(selectBody);
      this.lastMagnetizedObject = selectBody;
    } else {
      if (selectBody.objOsc.magnetic) {
        this.magneticField = updateCommonMagneticField(selectBody, this.magneticField);
        this.lastMagnetizedObject = selectBody;
      } else {
        updateMagneticFieldAnimation(this.lastMagnetizedObject, this.magneticField);
      }
    }
    this.updateShaders(this.firstBody, bodyClose);

    this.renderer.render(this.composition.scene, this.composition.camera);
    this.labelRenderer.render(this.scene, this.camera);

    if (this.commands.stats) {
      this.commands.stats.update();
    }

  }

  init() {
    this.renderer.setClearColor('#000000');
    this.createNoises();
    this.createComposition();
    this.configRenderCompo();

    this.orbitC.enablePan = true;
    this.orbitC.enableRotate = true;
    this.orbitC.enableZoom = true;
    this.orbitC.enableDamping = true;
    this.orbitC.dampingFactor = 0.25;
    this.orbitC.minDistance = 1;
    this.orbitC.maxDistance = 2e11;

    this.fly.rollSpeed = 0.3;
    this.fly.autoForward = false;
    this.fly.dragToLook = false;
    this.fly.enabled = false;

    this.camera.add(this.noises.plane.mesh);
  }

  createGui() {
    this.commands = new GuiInterface(this.camera, this.ambientLight);
  }

  configRenderCompo() {
    this.camera.aspect = this.sizes.width / this.sizes.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.composition.distortionRenderTarget.setSize(
      this.sizes.width * this.renderer.getPixelRatio(),
      this.sizes.height * this.renderer.getPixelRatio()
    );
    this.composition.defaultRenderTarget.setSize(
      this.sizes.width * this.renderer.getPixelRatio(),
      this.sizes.height * this.renderer.getPixelRatio()
    );
    if (this.labelRenderer) {
      this.labelRenderer.setSize(this.sizes.width - 20, this.sizes.height - 20);
    }
  }

  createNoises() {
    this.noises = {};
    this.noises.scene = this.scene;
    this.noises.camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.noises.camera.position.set(0, 0, 5);
    this.noises.scene.add(this.noises.camera);
    this.noises.plane = {};
    this.noises.plane.geometry = new PlaneGeometry(2, 2);
    this.noises.plane.material = new ShaderMaterial({
      vertexShader: noisesVertex,
      fragmentShader: noisesFragment,
      blending: NoBlending,
    });
    this.noises.plane.mesh = new Mesh(
      this.noises.plane.geometry,
      this.noises.plane.material
    );
    this.noises.scene.add(this.noises.plane.mesh);
    this.noises.renderTarget = new WebGLRenderTarget(128, 128, {
      generateMipmaps: false,
      type: FloatType,
      wrapS: RepeatWrapping,
      wrapT: RepeatWrapping,
      logarithmicDepthBuffer: true,
    });
    this.renderer.setRenderTarget(this.noises.renderTarget);
    this.renderer.render(this.noises.scene, this.noises.camera);
    this.renderer.setRenderTarget(null);
    return this.noises;
  }

  createComposition() {
    this.composition = {};
    this.composition.defaultRenderTarget = new WebGLRenderTarget(
      this.sizes.width * this.renderer.getPixelRatio(),
      this.sizes.height * this.renderer.getPixelRatio(),
      { generateMipmaps: false, logarithmicDepthBuffer: true }
    );
    this.composition.distortionRenderTarget = new WebGLRenderTarget(
      this.sizes.width * this.renderer.getPixelRatio(),
      this.sizes.height * this.renderer.getPixelRatio(),
      { generateMipmaps: false, logarithmicDepthBuffer: true }
    );
    this.composition.scene = new Scene();
    this.composition.camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.composition.camera.position.set(0, 0, 5);
    this.composition.scene.add(this.composition.camera);
    this.composition.plane = {};
    this.composition.plane.geometry = new PlaneGeometry(2, 2);
    this.composition.plane.material = new ShaderMaterial({
      vertexShader: compositionVertex,
      fragmentShader: compositionFragment,
      uniforms: {
        uDefaultTexture: { value: this.composition.defaultRenderTarget.texture },
        uDistortionTexture: { value: this.composition.distortionRenderTarget.texture },
        uConvergencePosition: { value: new Vector2() },
        uDirection: { value: -1 },
        uColorChange: { value: this.dimension }
      },
      depthTest: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    this.composition.plane.mesh = new Mesh(
      this.composition.plane.geometry,
      this.composition.plane.material
    );
    this.composition.scene.add(this.composition.plane.mesh);
    return this.composition;
  }

  createHoleDistortion() {
    this.distortion = {};
    this.distortion.scene = new Scene();
    this.distortion.hole = {};
    this.distortion.hole.geometry = new PlaneGeometry(
      this.sizeDistortion,
      this.sizeDistortion
    );
    this.distortion.hole.material = new ShaderMaterial({
      vertexShader: distortionHoleVertex,
      fragmentShader: distortionHoleFragment,
      depthTest: true,
      depthWrite: false,
      blending: NoBlending,
    });
    this.distortion.hole.mesh = new Mesh(
      this.distortion.hole.geometry,
      this.distortion.hole.material
    );
    this.distortion.scene.add(this.distortion.hole.mesh);
  }

  updateDistortion(element) {
    this.camera.updateMatrixWorld();
    const escala = element.size / this.sizeDistortion;
    this.distortion.hole.mesh.scale.set(element.scaleSpace * escala, element.scaleSpace * escala, 1);
    this.distortion.hole.mesh.position.copy(element.mesh.position);
    this.applyDistortion(
      element.mesh.position.x,
      element.mesh.position.y,
      element.mesh.position.z
    );
    this.composition.plane.material.uniforms.uDirection.value = element.distSpace;
  }

  updateShaders(first, element) {
    this.camera.updateMatrixWorld();
    if (Math.abs(element.distScale) > 0.25) {
      this.updateDistortion(element);
    } else {
      this.updateDistortion(first);
    }
    this.matCube.uniforms.time.value += 1e-4;
  }

  applyDistortion(x, y, z) {
    this.distortion.hole.mesh.lookAt(this.camera.position);
    const screenPosition = new Vector3(x, y, z).project(this.camera);
    screenPosition.x = screenPosition.x * 0.5 + 0.5;
    screenPosition.y = screenPosition.y * 0.5 + 0.5;
    this.composition.plane.material.uniforms.uConvergencePosition.value.set(
      screenPosition.x,
      screenPosition.y
    );
    this.renderer.render(this.distortion.scene, this.camera);

    this.renderer.setRenderTarget(this.composition.defaultRenderTarget);
    this.renderer.setClearColor("#130e16");
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(null);

    this.renderer.setRenderTarget(this.composition.distortionRenderTarget);
    this.renderer.setClearColor("#000000");
    this.renderer.render(this.distortion.scene, this.camera);
    this.renderer.setRenderTarget(null);
  }

  createLabel() {
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(this.sizes.width - 20, this.sizes.height - 20);
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0px';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    document.body.appendChild(this.labelRenderer.domElement);
  }

  addTexture() {
    this.sceneCube = new Scene();
    this.cubeRenderTarget1 = new WebGLCubeRenderTarget(256, {
      format: RGBFormat,
      generateMipmaps: true,
      minFilter: LinearMipmapLinearFilter,
      antialias: true,
      logarithmicDepthBuffer: true,
    });
    this.cubeCamera1 = new CubeCamera(0.01, 150, this.cubeRenderTarget1);
    this.matCube = new ShaderMaterial({
      side: BackSide,
      uniforms: {
        time: { value: 0 },
        resolution: { value: new Vector4() },
      },
      vertexShader: vertexCube,
      fragmentShader: fragmentCube,
    });
    this.geoCube = new SphereGeometry(1, 64, 64);
    this.meshCube = new Mesh(this.geoCube, this.matCube);
    this.sceneCube.add(this.meshCube);
  }
}
